import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export type InsightLevel = 'basic' | 'advanced' | 'full';

export interface SummaryInsight {
  totalReceitas: number;
  totalDespesas: number;
  saldo: number;
  savingsRate: number | null;
  transactionCount: number;
  topCategory: string | null;
  topCategoryAmount: number;
  topCategoryPercent: number;
  prevMonthDespesas: number;
  prevMonthReceitas: number;
  despesasChange: number | null;
  receitasChange: number | null;
}

export interface PatternInsight {
  id: string;
  type: 'warning' | 'success' | 'info' | 'tip';
  icon: string;
  title: string;
  message: string;
}

export interface CategoryPattern {
  category: string;
  total: number;
  percent: number;
  trend: 'up' | 'down' | 'stable';
  trendPct: number;
  color: string;
  count: number;
}

export interface MonthlyTrend {
  month: string;
  label: string;
  receitas: number;
  despesas: number;
  saldo: number;
}

export interface GoalInsight {
  id: string;
  name: string;
  progress: number;
  remaining: number;
  monthlyNeeded: number | null;
  pace: 'on_track' | 'behind' | 'at_risk' | 'overdue' | 'completed';
  message: string;
}

export interface CardInsight {
  cardName: string;
  totalSpent: number;
  limit: number | null;
  usagePercent: number | null;
  topCategory: string | null;
  message: string;
  type: 'warning' | 'success' | 'info';
}

export interface Recommendation {
  id: string;
  icon: string;
  priority: 'high' | 'medium' | 'low';
  title: string;
  message: string;
  action?: string;
}

export interface AdvancedInsightsData {
  summary: SummaryInsight;
  patterns: PatternInsight[];
  categoryPatterns: CategoryPattern[];
  monthlyTrends: MonthlyTrend[];
  goalInsights: GoalInsight[];
  cardInsights: CardInsight[];
  recommendations: Recommendation[];
}

const CATEGORY_COLORS = [
  '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4',
  '#F97316', '#EC4899', '#14B8A6', '#6366F1', '#84CC16',
];

const fmt = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const useAdvancedInsights = (
  level: InsightLevel = 'basic',
  maxInsights: number = 3,
  canAccessHistorical: boolean = false,
  canAccessRecommendations: boolean = false,
) => {
  const { user } = useAuth();
  const [data, setData] = useState<AdvancedInsightsData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);

    const today = new Date();
    const curStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
    const curEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
    const prevStart = new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().split('T')[0];
    const prevEnd = new Date(today.getFullYear(), today.getMonth(), 0).toISOString().split('T')[0];

    // Historical range: 6 months back
    const histStart = new Date(today.getFullYear(), today.getMonth() - 5, 1).toISOString().split('T')[0];

    const fetches: Promise<any>[] = [
      // CRÍTICO: deleted_at filter + transaction_subtype para excluir transferências dos cálculos
      supabase.from('transactions').select('type, amount, category, description, date, status, transaction_subtype').eq('user_id', user.id).gte('date', curStart).lte('date', curEnd).is('deleted_at', null),
      supabase.from('transactions').select('type, amount, category, status, transaction_subtype').eq('user_id', user.id).gte('date', prevStart).lte('date', prevEnd).is('deleted_at', null),
    ];

    if (canAccessHistorical) {
      fetches.push(
        supabase.from('transactions').select('type, amount, category, date, status, transaction_subtype').eq('user_id', user.id).gte('date', histStart).lte('date', curEnd).is('deleted_at', null),
      );
    }

    if (level !== 'basic') {
      fetches.push(
        supabase.from('goals').select('id, name, target_amount, current_amount, target_date, status, is_completed').eq('user_id', user.id).eq('status', 'active'),
        supabase.from('cards').select('id, name, credit_limit').eq('user_id', user.id),
        supabase.from('card_transactions').select('card_id, amount, category, status').eq('user_id', user.id).gte('date', curStart).lte('date', curEnd),
      );
    }

    const results = await Promise.all(fetches);
    let idx = 0;
    const curTxs = (results[idx++]?.data ?? []) as any[];
    const prevTxs = (results[idx++]?.data ?? []) as any[];
    const histTxs = canAccessHistorical ? (results[idx++]?.data ?? []) as any[] : [];
    const goalsData = level !== 'basic' ? (results[idx++]?.data ?? []) as any[] : [];
    const cardsData = level !== 'basic' ? (results[idx++]?.data ?? []) as any[] : [];
    const cardTxsData = level !== 'basic' ? (results[idx++]?.data ?? []) as any[] : [];

    // Summary
    // REGRA: usar apenas transações REALIZADAS para saldo, taxa de poupança e comparações
    // status='pago' → despesa realizada; status='recebido' → receita realizada
    // CRÍTICO: excluir transferências — não representam receita/despesa real
    const isTransfer = (t: any) => t.transaction_subtype === 'transferencia';
    const curExpenses = curTxs
      .filter(t => (t.type === 'expense' || t.type === 'despesa') && t.status === 'pago' && !isTransfer(t))
      .reduce((s: number, t: any) => s + Number(t.amount), 0);
    const curIncome = curTxs
      .filter(t => (t.type === 'income' || t.type === 'receita') && t.status === 'recebido' && !isTransfer(t))
      .reduce((s: number, t: any) => s + Number(t.amount), 0);
    const prevExpenses = prevTxs
      .filter(t => (t.type === 'expense' || t.type === 'despesa') && t.status === 'pago' && !isTransfer(t))
      .reduce((s: number, t: any) => s + Number(t.amount), 0);
    const prevIncome = prevTxs
      .filter(t => (t.type === 'income' || t.type === 'receita') && t.status === 'recebido' && !isTransfer(t))
      .reduce((s: number, t: any) => s + Number(t.amount), 0);

    const catMap: Record<string, number> = {};
    curTxs.filter(t => (t.type === 'expense' || t.type === 'despesa') && t.status === 'pago' && !isTransfer(t)).forEach((t: any) => {
      catMap[t.category] = (catMap[t.category] || 0) + Number(t.amount);
    });
    const sortedCats = Object.entries(catMap).sort((a, b) => b[1] - a[1]);
    const topCat = sortedCats[0] ?? null;

    // Only calculate savings rate when both income AND expenses exist — avoids
    // a misleading "100% poupança" alert when the user has only income with no
    // expenses yet in the period.
    const savingsRate = (curIncome > 0 && curExpenses > 0)
      ? ((curIncome - curExpenses) / curIncome) * 100
      : null;

    const despesasChange = prevExpenses > 0 ? ((curExpenses - prevExpenses) / prevExpenses) * 100 : null;
    const receitasChange = prevIncome > 0 ? ((curIncome - prevIncome) / prevIncome) * 100 : null;

    const summary: SummaryInsight = {
      totalReceitas: curIncome,
      totalDespesas: curExpenses,
      saldo: curIncome - curExpenses,
      savingsRate,
      transactionCount: curTxs.length,
      topCategory: topCat?.[0] ?? null,
      topCategoryAmount: topCat?.[1] ?? 0,
      topCategoryPercent: curExpenses > 0 && topCat ? Math.round((topCat[1] / curExpenses) * 100) : 0,
      prevMonthDespesas: prevExpenses,
      prevMonthReceitas: prevIncome,
      despesasChange,
      receitasChange,
    };

    // Category patterns (current vs prev)
    const prevCatMap: Record<string, number> = {};
    prevTxs.filter(t => (t.type === 'expense' || t.type === 'despesa') && t.status === 'pago' && !isTransfer(t)).forEach((t: any) => {
      prevCatMap[t.category] = (prevCatMap[t.category] || 0) + Number(t.amount);
    });

    const countMap: Record<string, number> = {};
    curTxs.filter(t => (t.type === 'expense' || t.type === 'despesa') && t.status === 'pago' && !isTransfer(t)).forEach((t: any) => {
      countMap[t.category] = (countMap[t.category] || 0) + 1;
    });

    const categoryPatterns: CategoryPattern[] = sortedCats.slice(0, 8).map(([cat, total], i) => {
      const prev = prevCatMap[cat] ?? 0;
      const trendPct = prev > 0 ? Math.round(((total - prev) / prev) * 100) : 0;
      const trend = trendPct > 5 ? 'up' : trendPct < -5 ? 'down' : 'stable';
      return {
        category: cat,
        total,
        percent: curExpenses > 0 ? Math.round((total / curExpenses) * 100) : 0,
        trend,
        trendPct: Math.abs(trendPct),
        color: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
        count: countMap[cat] ?? 0,
      };
    });

    // Patterns (textual insights)
    const patterns: PatternInsight[] = [];

    if (summary.saldo < 0) {
      patterns.push({ id: 'neg-balance', type: 'warning', icon: 'ri-alert-line', title: 'Gastos superaram receitas', message: `Este mês suas despesas (${fmt(curExpenses)}) superaram as receitas (${fmt(curIncome)}) em ${fmt(Math.abs(summary.saldo))}. Revise os gastos.` });
    } else if (savingsRate !== null && savingsRate >= 20) {
      // savingsRate is only non-null when both income and expenses exist
      patterns.push({ id: 'good-savings', type: 'success', icon: 'ri-shield-check-line', title: 'Boa taxa de poupança', message: `Você está poupando ${savingsRate.toFixed(0)}% da renda (${fmt(curIncome - curExpenses)}). Excelente! Isso é acima dos 20% recomendados.` });
    } else if (savingsRate !== null && savingsRate < 10 && savingsRate >= 0) {
      patterns.push({ id: 'low-savings', type: 'warning', icon: 'ri-wallet-line', title: 'Capacidade de poupança baixa', message: `Você está poupando apenas ${savingsRate.toFixed(0)}% da renda. Tente reduzir despesas variáveis para aumentar essa margem.` });
    }

    if (despesasChange !== null && despesasChange > 20) {
      patterns.push({ id: 'expenses-up', type: 'warning', icon: 'ri-arrow-up-circle-line', title: `Gastos subiram ${Math.round(despesasChange)}%`, message: `Em comparação ao mês anterior, seus gastos cresceram ${Math.round(despesasChange)}% (${fmt(curExpenses - prevExpenses)} a mais). Confira as categorias de maior crescimento.` });
    } else if (despesasChange !== null && despesasChange < -10) {
      patterns.push({ id: 'expenses-down', type: 'success', icon: 'ri-arrow-down-circle-line', title: `Gastos reduziram ${Math.abs(Math.round(despesasChange))}%`, message: `Ótimo! Você gastou ${Math.abs(Math.round(despesasChange))}% menos do que no mês passado (${fmt(Math.abs(curExpenses - prevExpenses))} a menos).` });
    }

    if (topCat && topCat[1] / curExpenses > 0.5) {
      patterns.push({ id: 'dominant-cat', type: 'info', icon: 'ri-pie-chart-line', title: `"${topCat[0]}" domina seus gastos`, message: `Esta categoria representa ${Math.round((topCat[1] / curExpenses) * 100)}% de todas as suas despesas do mês (${fmt(topCat[1])}). Vale avaliar se há espaço para redução.` });
    } else if (topCat) {
      patterns.push({ id: 'top-cat', type: 'info', icon: 'ri-price-tag-3-line', title: `Maior gasto: ${topCat[0]}`, message: `Você gastou ${fmt(topCat[1])} com ${topCat[0]} este mês, ${Math.round((topCat[1] / curExpenses) * 100)}% do total de despesas.` });
    }

    if (receitasChange !== null && receitasChange > 0) {
      patterns.push({ id: 'income-up', type: 'success', icon: 'ri-trending-up-line', title: `Receitas cresceram ${Math.round(receitasChange)}%`, message: `Suas receitas cresceram ${Math.round(receitasChange)}% em relação ao mês anterior. Considere aplicar parte desse crescimento em suas metas.` });
    }

    if (curTxs.length === 0) {
      patterns.push({ id: 'no-tx', type: 'tip', icon: 'ri-add-circle-line', title: 'Nenhum lançamento este mês', message: 'Registre suas movimentações para receber insights personalizados sobre seus hábitos financeiros.' });
    }

    // Monthly trends (6 months)
    const monthlyTrends: MonthlyTrend[] = [];
    if (canAccessHistorical) {
      for (let i = 5; i >= 0; i--) {
        const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
        const yr = d.getFullYear();
        const mo = d.getMonth() + 1;
        const label = d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
        const month = d.toLocaleDateString('pt-BR', { month: 'short' });
        const moTxs = histTxs.filter((t: any) => {
          const dt = new Date(t.date);
          return dt.getFullYear() === yr && dt.getMonth() + 1 === mo && t.transaction_subtype !== 'transferencia';
        });
        const rec = moTxs.filter((t: any) => t.type === 'income' || t.type === 'receita').reduce((s: number, t: any) => s + Number(t.amount), 0);
        const desp = moTxs.filter((t: any) => t.type === 'expense' || t.type === 'despesa').reduce((s: number, t: any) => s + Number(t.amount), 0);
        monthlyTrends.push({ month, label, receitas: rec, despesas: desp, saldo: rec - desp });
      }
    }

    // Goal insights
    const goalInsights: GoalInsight[] = [];
    if (level !== 'basic') {
      goalsData.forEach((g: any) => {
        const current = Number(g.current_amount ?? 0);
        const target = Number(g.target_amount ?? 0);
        const progress = target > 0 ? Math.min(100, Math.round((current / target) * 100)) : 0;
        const remaining = Math.max(0, target - current);

        let monthlyNeeded: number | null = null;
        let pace: GoalInsight['pace'] = 'on_track';
        let message = '';

        if (g.target_date) {
          const targetDate = new Date(g.target_date + 'T12:00:00');
          const now = new Date();
          const monthsLeft = Math.max(0, (targetDate.getFullYear() - now.getFullYear()) * 12 + (targetDate.getMonth() - now.getMonth()));

          if (targetDate < now && remaining > 0) {
            pace = 'overdue';
            message = `Meta vencida. Ainda faltam ${fmt(remaining)}. Revise a data ou reforce os aportes.`;
          } else if (monthsLeft > 0) {
            monthlyNeeded = remaining / monthsLeft;
            const savingsAvail = summary.saldo > 0 ? summary.saldo : 0;
            if (progress >= 100) {
              pace = 'completed';
              message = 'Meta concluída! Parabéns!';
            } else if (savingsAvail > 0 && monthlyNeeded > savingsAvail * 1.2) {
              pace = 'behind';
              message = `Para atingir esta meta, seria necessário poupar ${fmt(monthlyNeeded)}/mês — acima da sua capacidade atual.`;
            } else if (savingsAvail > 0 && monthlyNeeded > savingsAvail * 0.9) {
              pace = 'at_risk';
              message = `Sua capacidade de poupança está justa para esta meta. Aporte ${fmt(monthlyNeeded)}/mês para não atrasar.`;
            } else {
              pace = 'on_track';
              message = `No ritmo certo! Aporte ${fmt(monthlyNeeded)}/mês para concluir no prazo.`;
            }
          }
        } else {
          pace = progress >= 100 ? 'completed' : 'on_track';
          message = progress >= 100 ? 'Meta concluída!' : `${progress}% concluído. Continue contribuindo regularmente.`;
        }

        goalInsights.push({ id: g.id, name: g.name, progress, remaining, monthlyNeeded, pace, message });
      });
    }

    // Card insights
    const cardInsights: CardInsight[] = [];
    if (level !== 'basic' && cardsData.length > 0) {
      cardsData.forEach((card: any) => {
        const txs = cardTxsData.filter((t: any) => t.card_id === card.id);
        const spent = txs.reduce((s: number, t: any) => s + Number(t.amount), 0);
        const limit = card.credit_limit ? Number(card.credit_limit) : null;
        const usagePercent = limit && limit > 0 ? Math.round((spent / limit) * 100) : null;

        const catCountCard: Record<string, number> = {};
        txs.forEach((t: any) => { catCountCard[t.category] = (catCountCard[t.category] || 0) + Number(t.amount); });
        const topCardCat = Object.entries(catCountCard).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;

        let type: CardInsight['type'] = 'info';
        let message = '';
        if (usagePercent !== null && usagePercent >= 80) {
          type = 'warning';
          message = `Uso do limite em ${usagePercent}%. Cuidado para não comprometer o próximo mês.`;
        } else if (usagePercent !== null && usagePercent >= 50) {
          type = 'info';
          message = `Uso do limite em ${usagePercent}%. Ainda há margem, mas acompanhe os gastos.`;
        } else if (spent > 0) {
          type = 'success';
          message = `Gastos controlados em ${usagePercent !== null ? usagePercent + '% do limite' : fmt(spent)}.`;
        } else {
          message = 'Nenhum gasto registrado neste cartão este mês.';
        }

        cardInsights.push({ cardName: card.name, totalSpent: spent, limit, usagePercent, topCategory: topCardCat, message, type });
      });
    }

    // Recommendations
    const recommendations: Recommendation[] = [];
    if (canAccessRecommendations) {
      if (savingsRate !== null && savingsRate < 10) {
        recommendations.push({ id: 'rec-savings', icon: 'ri-piggy-bank-line', priority: 'high', title: 'Aumente sua taxa de poupança', message: 'Tente chegar a pelo menos 20% da renda guardada por mês. Comece reduzindo a maior categoria de gastos variáveis.' });
      }
      if (topCat && topCat[1] / curExpenses > 0.4) {
        recommendations.push({ id: 'rec-cat', icon: 'ri-scissors-cut-line', priority: 'high', title: `Revise gastos com ${topCat[0]}`, message: `Esta categoria representa mais de 40% dos seus gastos. Uma redução de 20% aqui teria grande impacto no seu saldo mensal.` });
      }
      const overdueGoals = goalInsights.filter(g => g.pace === 'overdue' || g.pace === 'behind');
      if (overdueGoals.length > 0) {
        recommendations.push({ id: 'rec-goals', icon: 'ri-focus-3-line', priority: 'medium', title: 'Metas em risco de prazo', message: `${overdueGoals.length} meta(s) precisam de atenção. Considere revisar os prazos ou aumentar os aportes mensais.` });
      }
      const highUsageCards = cardInsights.filter(c => c.usagePercent !== null && (c.usagePercent ?? 0) >= 70);
      if (highUsageCards.length > 0) {
        recommendations.push({ id: 'rec-card', icon: 'ri-bank-card-line', priority: 'medium', title: 'Limite de cartão elevado', message: `${highUsageCards.map(c => c.cardName).join(', ')} com uso alto. Controlar o cartão reduz o risco de endividamento.` });
      }
      if (summary.saldo > 0 && goalInsights.length === 0) {
        recommendations.push({ id: 'rec-no-goals', icon: 'ri-flag-line', priority: 'low', title: 'Você tem capacidade de poupança', message: `Você tem saldo positivo de ${fmt(summary.saldo)} este mês. Que tal criar uma meta e direcionar esse valor para um objetivo?`, action: '/metas' });
      }
      if (receitasChange !== null && receitasChange > 15) {
        recommendations.push({ id: 'rec-income-up', icon: 'ri-bar-chart-grouped-line', priority: 'low', title: 'Aproveite o crescimento de receita', message: `Suas receitas cresceram ${Math.round(receitasChange)}%. Destine parte desse crescimento para acelerar suas metas ou reserva de emergência.` });
      }
    }

    setData({
      summary,
      patterns: patterns.slice(0, maxInsights),
      categoryPatterns,
      monthlyTrends,
      goalInsights,
      cardInsights,
      recommendations,
    });
    setLoading(false);
  }, [user, level, maxInsights, canAccessHistorical, canAccessRecommendations]);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, refetch: fetch };
};
